import styles from './index.less';
import { Cell, Tag, Typography, Skeleton, Toast } from 'react-vant';
import { useLocation, useModel } from '@umijs/max';
import { useEffect, useState } from 'react';
import { AssetDetailEnum } from '@/pages/Mine/config';
import { useSafeState } from 'ahooks';
import { CarType, ParkType, parkTypeMap } from '@/pages/Mine/interface';
import { queryOwnerAssetDetail, querySetDefaultHouse } from '@/service/house';
import StickyActiveButton from '@/components/StickyActiveButton';
import BindHouse from '@/pages/Mine/My/components/BindHouse';
import NoData from '@/components/NoData';

const AssetDetail = () => {
  const location = useLocation();
  const { type } = location.state as { type: string };
  const [carDetail, setCarDetail] = useSafeState<CarType[]>([]);
  const [parkDetail, setParkDetail] = useSafeState<ParkType[]>([]);
  const [loading, setLoading] = useSafeState(false);
  const [bindHouseVisible, setBindHouseVisible] = useState<boolean>(false);
  const { roomList, queryInformation, defaultRoom } = useModel(
    'useDefaultRoom',
    (model) => ({
      ownerInformation: model.ownerInformation,
      roomList: model.roomList,
      queryInformation: model.queryInformation,
      defaultRoom: model.defaultRoom,
    }),
  );

  const loadData = () => {
    setLoading(true);
    queryOwnerAssetDetail(defaultRoom ? defaultRoom?.roomId : '')
      .then((res) => {
        if (res?.code === 200) {
          if (res?.data?.car) {
            setCarDetail(res?.data?.car);
          }
          if (res?.data?.park) {
            setParkDetail(res?.data?.park);
          }
        }
      })
      .finally(() => setLoading(false));
  };

  const handleBindHouse = () => {
    setBindHouseVisible(true);
  };

  const handleSetDefaultRoom = (roomId: string) => {
    querySetDefaultHouse(roomId).then((res) => {
      if (res?.code === 200) {
        Toast.success('设置成功！');
        queryInformation();
      }
    });
  };

  useEffect(() => {
    loadData();
  }, [type]);

  return (
    <Skeleton loading={loading}>
      {/*更多房屋列表*/}
      {type === AssetDetailEnum.HOUSE && (
        <>
          {roomList.length ? (
            roomList.map((item, index) => (
              <div className={styles.container} key={item.sequenceNbr}>
                <div className={styles.title}>
                  <Typography.Title style={{ marginBottom: 0 }}>
                    房产{index + 1}
                  </Typography.Title>
                  {index === 0 ? (
                    <Tag
                      round
                      style={{ '--rv-tag-padding': '4px 8px' }}
                      type={'primary'}
                    >
                      当前
                    </Tag>
                  ) : (
                    <Tag
                      round
                      style={{ '--rv-tag-padding': '4px 8px' }}
                      type={'primary'}
                      onClick={() => handleSetDefaultRoom(item.roomId)}
                    >
                      切换
                    </Tag>
                  )}
                </div>
                <Cell.Group card>
                  <Cell title={'小区'} value={item.communityName} />
                  <Cell title={'房号'} value={item.roomName} />
                  <Cell title={'业主名称'} value={item.customerName} />
                  <Cell title={'联系电话'} value={item.customerPhone} />
                  <Cell title={'审核状态'} value={'审核通过'} />
                </Cell.Group>
              </div>
            ))
          ) : (
            <NoData />
          )}
        </>
      )}
      {/*更多车位列表*/}
      {type === AssetDetailEnum.PARKING_LOT && (
        <>
          {parkDetail.length ? (
            parkDetail.map((item, index: number) => (
              <div className={styles.container} key={item.sequenceNbr}>
                <div className={styles.title}>
                  <Typography.Title style={{ marginBottom: 0 }}>
                    车位{index + 1}
                  </Typography.Title>
                  {index === 0 ? (
                    <Tag
                      round
                      style={{ '--rv-tag-padding': '4px 8px' }}
                      type={'primary'}
                    >
                      当前
                    </Tag>
                  ) : (
                    <Tag
                      round
                      style={{ '--rv-tag-padding': '4px 8px' }}
                      type={'primary'}
                    >
                      切换
                    </Tag>
                  )}
                </div>
                <Cell.Group card>
                  <Cell title={'小区'} value={defaultRoom?.communityName} />
                  <Cell title={'编号'} value={item.parkCode} />
                  <Cell title={'车位类型'} value={parkTypeMap[item.parkType]} />
                  <Cell title={'绑定车辆数'} value={item.carNum} />
                </Cell.Group>
              </div>
            ))
          ) : (
            <NoData />
          )}
        </>
      )}
      {/*更多车辆列表*/}
      {type === AssetDetailEnum.CARPORT && (
        <>
          {carDetail.length ? (
            carDetail.map((item, index: number) => (
              <div className={styles.container} key={item.sequenceNbr}>
                <div className={styles.title}>
                  <Typography.Title style={{ marginBottom: 0 }}>
                    车辆{index + 1}
                  </Typography.Title>
                  {index === 0 ? (
                    <Tag
                      round
                      style={{ '--rv-tag-padding': '4px 8px' }}
                      type={'primary'}
                    >
                      当前
                    </Tag>
                  ) : (
                    <Tag
                      round
                      style={{ '--rv-tag-padding': '4px 8px' }}
                      type={'primary'}
                    >
                      切换
                    </Tag>
                  )}
                </div>
                <Cell.Group card>
                  <Cell title={'小区'} value={defaultRoom?.communityName} />
                  <Cell title={'绑定车位'} value={item.parkCode} />
                  <Cell title={'车牌号'} value={item.carCode} />
                  <Cell title={'车辆颜色'} value={item.color || '白色'} />
                </Cell.Group>
              </div>
            ))
          ) : (
            <NoData />
          )}
        </>
      )}
      {type === AssetDetailEnum.HOUSE && (
        <StickyActiveButton
          activeText={'绑定房屋'}
          activeClick={handleBindHouse}
        />
      )}
      <BindHouse
        visible={bindHouseVisible}
        close={() => setBindHouseVisible(false)}
      />
    </Skeleton>
  );
};

export default AssetDetail;
